import {Component, forwardRef, input, output, viewChild} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors} from '@angular/forms';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {SimpleOption} from '@app/shared/model/simple-option';
import {MessageTranslateService} from '@app/shared/services/i18n/translate-service-service.service';
import {AceSelectComponent} from '../ace-select/ace-select.component';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {DAYS_30, DAYS_60} from '@app/shared/util/general/duration-utils';
import {AppConstants} from '@shared/util/app-constants';

export enum DurationOptions {
  GT_EQ_60D = '>=PT60D',
  GT_EQ_30D_LT_60D = '>=PT30D<PT60D',
  LT_30D = '<PT30D'
}


export interface DurationRequestValues {
  durationGt?: number;
  durationGtEq?: number;
  durationLt?: number;
  durationLtEq?: number;
}


const DEFAULT_MAX_SELECTED_ITEM_NAME: I18nMessage = {messageKey: 'duration options'};
const INDEX_BY_DURATION = new Map(Object.values(DurationOptions).reverse().map((option, i) => [option, i]));
const DISJOINT_DURATION_ERROR: ValidationErrors = {disjointDuration: {messageKey: 'Duration ranges must be contiguous'}};

const options: SimpleOption<DurationOptions, I18nMessage>[] = [
  {value: DurationOptions.GT_EQ_60D, label: {messageKey: 'count days', parameters: {count: '>=60'}}},
  {value: DurationOptions.GT_EQ_30D_LT_60D, label: {messageKey: 'count days', parameters: {count: '30-60'}}},
  {value: DurationOptions.LT_30D, label: {messageKey: 'count days', parameters: {count: '<30'}}}
]

export const messageByDurationOptions = CollectionUtils.indexBy(options, 'value', option => option.label);

type Value = DurationOptions | DurationOptions[] | null;

@Component({
  selector: 'app-duration-select',
  templateUrl: './duration-select.component.html',
  standalone: false,
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DurationSelectComponent), multi: true}
  ]
})
export class DurationSelectComponent implements ControlValueAccessor {
  options: SimpleOption<DurationOptions>[];
  readonly valueKey: keyof SimpleOption = 'value';
  readonly labelKey: keyof SimpleOption = 'label';
  readonly DROPDOWN_HOLDER_SELECTOR = AppConstants.DROPDOWN_HOLDER_SELECTOR;

  multiSelect = input(false);
  maxSelectedLabels = input(1);
  maxSelectedItemName = input<I18nMessage>(DEFAULT_MAX_SELECTED_ITEM_NAME);
  placeholder = input<string | null>(null);
  openEvent = output<any>();
  closeEvent = output<any>();
  aceSelect = viewChild.required(AceSelectComponent);

  constructor(translateService: MessageTranslateService) {
    this.options = options.map(option => ({...option, label: translateService.instant(option.label)}));
  }

  writeValue(value: Value): void {
    this.aceSelect().writeValue(value);
  }

  registerOnChange(fn: (value: Value) => void): void {
    this.aceSelect().registerOnChange(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.aceSelect().registerOnTouched(fn);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.aceSelect().setDisabledState(isDisabled);
  }

  onOpenEvent(event: any): void {
    this.openEvent.emit(event);
  }

  onCloseEvent(event: any): void {
    this.closeEvent.emit(event);
  }

  static contiguousValidator(control: AbstractControl): ValidationErrors | null {
    return DurationSelectComponent.isContiguous(control.value) ? null : DISJOINT_DURATION_ERROR;
  }

  /** Checks if selections are contiguous according to `INDEX_BY_DURATION`
   * [0, 1, 2]   range=3, count=3  → equal ✓
   * [0, 1, 3]   range=4, count=3  → 4 > 3 ✗  (gap between 1 and 3)
   * @param durations unique options selected
   * @returns true if contiguous
   */
  private static isContiguous(durations: Value): boolean {
    if (!durations || !Array.isArray(durations) || durations.length <= 1) {
      return true;
    }
    const indices = durations.map(duration => INDEX_BY_DURATION.get(duration)).sort((index1, index2) => index1 - index2);
    const lastIndex = indices.length - 1;
    return (indices[lastIndex] - indices[0]) === lastIndex;
  }

  /** Merges contiguous age filter selections into one request value using the endpoints of the sorted range.
   * @param durationOptions - a contiguous durationOption
   * @returns ticket request duration-filter information
   */
  public static toMergedRequestValue(durationOptions: DurationOptions[] | null): DurationRequestValues | null {
    if (!durationOptions?.length) {
      return null;
    }
    if (!DurationSelectComponent.isContiguous(durationOptions)) {
      throw new Error('Expected contiguous durations but got: ' + durationOptions?.join(', '))
    }
    const sorted = [...durationOptions].sort((option1, option2) => INDEX_BY_DURATION.get(option1) - INDEX_BY_DURATION.get(option2));
    const first = DurationSelectComponent.toRequestValue(sorted[0]);
    const last = DurationSelectComponent.toRequestValue(sorted[sorted.length - 1]);
    const result: DurationRequestValues = {};
    if (first.durationGtEq != null) {
      result.durationGtEq = first.durationGtEq;
    }
    if (last.durationLt != null) {
      result.durationLt = last.durationLt;
    }
    return (result.durationGtEq != null || result.durationLt != null) ? result : null;
  }

  private static toRequestValue(durationOption: DurationOptions): DurationRequestValues {
    if (durationOption === DurationOptions.GT_EQ_60D) {
      return {durationGtEq: DAYS_60};
    } else if (durationOption === DurationOptions.GT_EQ_30D_LT_60D) {
      return {durationGtEq: DAYS_30, durationLt: DAYS_60};
    } else if (durationOption === DurationOptions.LT_30D) {
      return {durationLt: DAYS_30};
    } else if (!durationOption) {
      return null;
    } else {
      throw new Error('Invalid duration: ' + JSON.stringify(durationOption));
    }
  }
}
